import React, { useState, useMemo } from "react";
import ProtectedRoute from "../components/ProtectedRoute";
import {
    useGetGameLoggerListQuery,
    useGetGameLoggerStatsQuery,
    useLazyGetGameLoggerDetailQuery,
    GameLoggerListParams,
    GameLoggerFullRecord,
} from "../api/gameLogger";

const GameLoggerPage: React.FC = () => {
    const [userId, setUserId] = useState<string>("");
    const [method, setMethod] = useState<string>("");
    const [status, setStatus] = useState<string>("");
    const [endpoint, setEndpoint] = useState<string>("");
    const [dateFrom, setDateFrom] = useState<string>("");
    const [dateTo, setDateTo] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(20);
    const [sortBy, setSortBy] = useState<GameLoggerListParams["sort_by"]>("created_at");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    const [selectedId, setSelectedId] = useState<number | null>(null);

    const params: GameLoggerListParams = useMemo(() => {
        const parsedUserId = parseInt(userId, 10);
        return {
            user_id: !Number.isNaN(parsedUserId) && parsedUserId > 0 ? parsedUserId : undefined,
            method: method || undefined,
            status: status || undefined,
            endpoint: endpoint || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            page,
            per_page: perPage,
            sort_by: sortBy,
            sort_order: sortOrder,
        };
    }, [userId, method, status, endpoint, dateFrom, dateTo, page, perPage, sortBy, sortOrder]);

    const statsParams = useMemo(
        () => ({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
        [dateFrom, dateTo]
    );

    const { data, isLoading, error } = useGetGameLoggerListQuery(params);
    const { data: statsData } = useGetGameLoggerStatsQuery(statsParams);
    const [getDetail, { data: detailResult, isLoading: detailLoading }] = useLazyGetGameLoggerDetailQuery();

    const openDetailModal = (id: number) => {
        setSelectedId(id);
        getDetail(id);
    };

    const closeDetailModal = () => {
        setSelectedId(null);
    };

    const detailRecord: GameLoggerFullRecord | null =
        detailResult?.success && detailResult?.data && detailResult.data.id === selectedId
            ? detailResult.data
            : null;

    const handleSort = (field: NonNullable<GameLoggerListParams["sort_by"]>) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortOrder("desc");
        }
        setPage(1);
    };

    const getStatusBadgeColor = (s: string) => {
        const code = parseInt(s, 10);
        if (code >= 200 && code < 300) return "bg-green-500";
        if (code >= 400 && code < 500) return "bg-yellow-500";
        if (code >= 500) return "bg-red-500";
        return "bg-gray-500";
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleString("ru-RU");
    };

    const logs = data?.data || [];
    const meta = data?.meta;
    const stats = statsData?.data;

    return (
        <ProtectedRoute requiredRoles={["admin"]}>
            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Game API логи
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Логирование запросов к Game API (игра Миллениум)
                    </p>
                </div>

                {/* Карточки статистики */}
                {stats && (
                    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Всего запросов
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                {stats.total}
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Успешные (2xx)
                            </div>
                            <div className="text-2xl font-bold text-green-600">
                                {stats.success_count}
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Ошибки клиента (4xx)
                            </div>
                            <div className="text-2xl font-bold text-yellow-600">
                                {stats.client_error_count}
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Ошибки сервера (5xx)
                            </div>
                            <div className="text-2xl font-bold text-red-600">
                                {stats.server_error_count}
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Топ пользователей
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                {stats.top_users?.length ?? 0}
                            </div>
                        </div>
                    </div>
                )}

                {/* Фильтры */}
                <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                User ID
                            </label>
                            <input
                                type="number"
                                value={userId}
                                onChange={(e) => {
                                    setUserId(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                placeholder="ID пользователя"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Method
                            </label>
                            <select
                                value={method}
                                onChange={(e) => {
                                    setMethod(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            >
                                <option value="">Все</option>
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="PATCH">PATCH</option>
                                <option value="DELETE">DELETE</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Status
                            </label>
                            <input
                                type="text"
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                placeholder="200, 404, 500..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Endpoint
                            </label>
                            <input
                                type="text"
                                value={endpoint}
                                onChange={(e) => {
                                    setEndpoint(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                placeholder="Часть пути"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Дата от
                            </label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => {
                                    setDateFrom(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Дата до
                            </label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => {
                                    setDateTo(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                            />
                        </div>
                        <div className="flex items-end">
                            <button
                                onClick={() => {
                                    setUserId("");
                                    setMethod("");
                                    setStatus("");
                                    setEndpoint("");
                                    setDateFrom("");
                                    setDateTo("");
                                    setPage(1);
                                }}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                            >
                                Сбросить
                            </button>
                        </div>
                    </div>
                </div>

                {/* Таблица */}
                {error && (
                    <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg">
                        Ошибка загрузки:{" "}
                        {error instanceof Error
                            ? error.message
                            : "Неизвестная ошибка"}
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th
                                        onClick={() => handleSort("id")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        ID{" "}
                                        {sortBy === "id" &&
                                            (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th
                                        onClick={() => handleSort("user_id")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        User ID{" "}
                                        {sortBy === "user_id" &&
                                            (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th
                                        onClick={() => handleSort("endpoint")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Endpoint{" "}
                                        {sortBy === "endpoint" &&
                                            (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th
                                        onClick={() => handleSort("method")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Method{" "}
                                        {sortBy === "method" &&
                                            (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th
                                        onClick={() => handleSort("status")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Status{" "}
                                        {sortBy === "status" &&
                                            (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th
                                        onClick={() => handleSort("created_at")}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        Дата{" "}
                                        {sortBy === "created_at" &&
                                            (sortOrder === "asc" ? "↑" : "↓")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {isLoading ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                                        >
                                            Загрузка...
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                                        >
                                            Нет данных
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr
                                            key={log.id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.user_id ?? "—"}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs truncate">
                                                {log.endpoint}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {log.method}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`px-2 py-1 text-xs font-semibold rounded-full text-white ${getStatusBadgeColor(
                                                        log.status,
                                                    )}`}
                                                >
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {formatDate(log.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() =>
                                                        openDetailModal(log.id)
                                                    }
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                                                >
                                                    Подробнее
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Пагинация */}
                    {meta && meta.last_page > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                    Показано{" "}
                                    {meta.total > 0
                                        ? (meta.current_page - 1) *
                                              meta.per_page +
                                          1
                                        : 0}{" "}
                                    -{" "}
                                    {Math.min(
                                        meta.current_page * meta.per_page,
                                        meta.total,
                                    )}{" "}
                                    из {meta.total}
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <label className="text-sm text-gray-700 dark:text-gray-300">
                                            На странице:
                                        </label>
                                        <select
                                            value={meta.per_page}
                                            onChange={(e) => {
                                                setPerPage(
                                                    Number(e.target.value),
                                                );
                                                setPage(1);
                                            }}
                                            className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() =>
                                                setPage(Math.max(1, page - 1))
                                            }
                                            disabled={page === 1}
                                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Назад
                                        </button>
                                        <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                            Страница {meta.current_page} из{" "}
                                            {meta.last_page}
                                        </span>
                                        <button
                                            onClick={() =>
                                                setPage(
                                                    Math.min(
                                                        meta.last_page,
                                                        page + 1,
                                                    ),
                                                )
                                            }
                                            disabled={page === meta.last_page}
                                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Вперед
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Топ пользователей и эндпоинтов */}
                {stats &&
                    (stats.top_users?.length > 0 ||
                        stats.top_endpoints?.length > 0) && (
                        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {stats.top_users?.length > 0 && (
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                        Топ пользователей по активности
                                    </h3>
                                    <ul className="space-y-2">
                                        {stats.top_users.map((u, i) => (
                                            <li
                                                key={u.user_id}
                                                className="flex justify-between text-sm"
                                            >
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {i + 1}. User ID {u.user_id}
                                                </span>
                                                <span className="font-medium text-gray-900 dark:text-white">
                                                    {u.count}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {stats.top_endpoints?.length > 0 && (
                                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                        Топ эндпоинтов
                                    </h3>
                                    <ul className="space-y-2">
                                        {stats.top_endpoints.map((e, i) => (
                                            <li
                                                key={e.endpoint + i}
                                                className="flex justify-between text-sm"
                                            >
                                                <span className="text-gray-700 dark:text-gray-300 truncate max-w-[70%]">
                                                    {i + 1}. {e.endpoint}
                                                </span>
                                                <span className="font-medium text-gray-900 dark:text-white shrink-0 ml-2">
                                                    {e.count}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            {/* Модалка с деталями */}
            {selectedId !== null && (
                <div
                    className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
                    onClick={closeDetailModal}
                >
                    <div
                        className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                Запись #{selectedId}
                            </h3>
                            <button
                                onClick={closeDetailModal}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        {detailLoading ? (
                            <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                                Загрузка...
                            </div>
                        ) : detailRecord ? (
                            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                        Основные данные
                                    </h4>
                                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm">
                                        <div className="grid grid-cols-2 gap-2">
                                            <span className="text-gray-500 dark:text-gray-400">
                                                User ID:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {detailRecord.user_id ?? "—"}
                                            </span>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Endpoint:
                                            </span>
                                            <span className="text-gray-900 dark:text-white break-all">
                                                {detailRecord.endpoint}
                                            </span>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Method:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {detailRecord.method}
                                            </span>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Status:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {detailRecord.status}
                                            </span>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                Created:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {formatDate(
                                                    detailRecord.created_at,
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {detailRecord.params && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                            Параметры запроса
                                        </h4>
                                        <pre className="bg-gray-50 dark:bg-gray-700 dark:text-gray-200 p-3 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto">
                                            {JSON.stringify(
                                                detailRecord.params,
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    </div>
                                )}

                                {detailRecord.response !== null &&
                                    detailRecord.response !== undefined && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                Ответ
                                            </h4>
                                            <pre className="bg-gray-50 dark:bg-gray-700 dark:text-gray-200 p-3 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto">
                                                {typeof detailRecord.response ===
                                                "object"
                                                    ? JSON.stringify(
                                                          detailRecord.response,
                                                          null,
                                                          2,
                                                      )
                                                    : String(
                                                          detailRecord.response,
                                                      )}
                                            </pre>
                                        </div>
                                    )}

                                {detailRecord.error && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">
                                            Ошибка
                                        </h4>
                                        <pre className="bg-red-50 dark:bg-red-900/20  p-3 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto text-red-800 dark:text-red-200">
                                            {JSON.stringify(
                                                detailRecord.error,
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                                Не удалось загрузить данные
                            </div>
                        )}
                    </div>
                </div>
            )}
        </ProtectedRoute>
    );
};

export default GameLoggerPage;
